import React, { useEffect } from 'react';
import { X, Calendar, Clock, MapPin, User, FileText, Stethoscope } from 'lucide-react';

const BookingDetailsPopup = ({ booking, isOpen, onClose }) => {
  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && event.target.classList.contains('popup-overlay')) {
        onClose();
      }
    };

    // Close popup when pressing Escape key
    const handleEscapeKey = (event) => {
      if (isOpen && event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset'; // Restore scrolling
    };
  }, [isOpen, onClose]);

  if (!isOpen || !booking) return null;

  // Format date and time
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div className="popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg flex items-center justify-between">
          <h2 className="text-lg font-semibold">Upcoming Booking Details</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3 max-h-[calc(85vh-120px)] overflow-y-auto">
          {/* Booking ID */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <FileText className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Booking ID</span>
            </div>
            <p className="text-base font-semibold text-gray-900">{booking.booking_id || 'N/A'}</p>
          </div>

          {/* Main Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reason for Appointment */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Reason for Appointment</span>
              </div>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md text-sm">
                {booking.reason || 'Not specified'}
              </p>
            </div>

            {/* Appointment Type */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Stethoscope className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Appointment Type</span>
              </div>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md text-sm">
                {booking.appointment_type || 'N/A'}
              </p>
            </div>

            {/* Schedule Type */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Schedule Type</span>
              </div>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md text-sm">
                {booking.schedule_type || 'N/A'}
              </p>
            </div>

            {/* Specialty */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Stethoscope className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Specialty</span>
              </div>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md text-sm">
                {booking.specialty || 'N/A'}
              </p>
            </div>

            {/* Doctor Name */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Doctor Name</span>
              </div>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md text-sm">
                {booking.doctor || 'N/A'}
              </p>
            </div>

            {/* Clinic Location */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Clinic Location</span>
              </div>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md text-sm">
                {booking.clinic_location || 'N/A'}
              </p>
            </div>

            {/* Appointment Date */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Appointment Date</span>
              </div>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md text-sm">
                {formatDate(booking.appointment_date)}
              </p>
            </div>

            {/* Appointment Time */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Appointment Time</span>
              </div>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md text-sm">
                {formatTime(booking.appointment_time)}
              </p>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-blue-600"></div>
              <span className="text-sm font-medium text-gray-600">Status</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                booking.status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : booking.status === 'cancelled'
                  ? 'bg-red-100 text-red-800'
                  : booking.status === 'Scheduled'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {booking.status === 'completed' ? 'Completed' : 
                 booking.status === 'cancelled' ? 'Cancelled' : 
                 booking.status}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 rounded-b-lg flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailsPopup; 